import type { WeeklyReimbursementsReportListParams } from '../types/pages'
/**
 * Weekly Reimbursements Report — Angular `ReportsFinancialMRGComponent`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Download, Loader2 } from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { ListSearchToolbar } from '@/components/ui/ListSearchToolbar'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { showApiError } from '@/lib/utils/notifications'
import { formatAmount } from '@/lib/utils/formatters'
import { cn } from '@lib/utils/helpers'
import { ReportTableScroll } from '../components/ReportTableScroll'
import reportStyles from '../styles/reportsTable.module.css'
import { REPORT_EXPORT_BUTTON_CLASS } from '../constants/configuredReportStyles'
import {
  WeeklyReimbursementsFilters,
  type WeeklyReimbursementsFilterState,
} from '../components/WeeklyReimbursementsFilters'
import { WeeklyReimbursementsSummaryOverview } from '../components/WeeklyReimbursementsSummaryOverview'
import { getWeeklyReimbursementList } from '../services/reportsService'
import type { WeeklyReimbursementListResponse, WeeklyReimbursementRow } from '../types/api'
import {
  buildWeeklyReimbursementsExportParams,
  buildReportExportUrl,
} from '../utils/reportExportUrls'
import { appendWeeklyReimbursementsListParams } from '../utils/reportQueryParams'
import {
  getRolling52WeekRange,
  yearBoundsYmd,
} from '../utils/reportRolling52Weeks'
import {
  buildWeeklyReimbursementsSessionFromParams,
  clearWeeklyReimbursementsSession,
  readWeeklyReimbursementsSession,
  sessionFilterToWeeklyReimbursementsPatch,
  writeWeeklyReimbursementsSession,
} from '../utils/weeklyReimbursementsSessionFilter'
import { DEFAULT_PARAMS } from '../constants/pages/weeklyReimbursementsReportPage'

function initialParams(): WeeklyReimbursementsReportListParams {
  const session = readWeeklyReimbursementsSession()
  if (session) {
    return {
      ...DEFAULT_PARAMS,
      ...sessionFilterToWeeklyReimbursementsPatch(session),
    }
  }
  return { ...DEFAULT_PARAMS }
}

function countActiveWeeklyFilters(f: WeeklyReimbursementsFilterState): number {
  let n = 0
  if (f.year && f.year !== 'Rolling 52 Weeks') n++
  if (f.fromDt || f.toDt) n++
  if (f.week_filter.length) n++
  return n
}

function applyYearChange(
  year: string,
  current: WeeklyReimbursementsFilterState,
): WeeklyReimbursementsFilterState {
  if (year === 'Rolling 52 Weeks') {
    return { ...current, year, ...getRolling52WeekRange() }
  }
  const y = Number(year)
  if (!Number.isNaN(y)) {
    return { ...current, year, ...yearBoundsYmd(y) }
  }
  return { ...current, year }
}

function pct(value: string | number | undefined): string {
  if (value == null || value === '') return '0%'
  const n = typeof value === 'string' ? parseFloat(value) : value
  if (Number.isNaN(n)) return '0%'
  return `${n.toFixed(2)}%`
}

export function WeeklyReimbursementsReportPage() {
  const [params, setParams] = useState<WeeklyReimbursementsReportListParams>(initialParams)
  const [searchQuery, setSearchQuery] = useState('')
  const [rows, setRows] = useState<WeeklyReimbursementRow[]>([])
  const [reportData, setReportData] =
    useState<WeeklyReimbursementListResponse | null>(null)
  const [pageMeta, setPageMeta] = useState({ current: 1, last: 1, total: 0 })
  const [loading, setLoading] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)

  const filterState: WeeklyReimbursementsFilterState = useMemo(
    () => ({
      year: params.year,
      fromDt: params.fromDt,
      toDt: params.toDt,
      week_filter: params.week_filter,
    }),
    [params],
  )

  const displayedRows = useMemo(() => {
    if (!searchQuery.trim()) return rows
    const q = searchQuery.toLowerCase().trim()
    return rows.filter((r) => {
      const matchStart = r.start_date ? r.start_date.toLowerCase().includes(q) : false
      const matchEnd = r.end_date ? r.end_date.toLowerCase().includes(q) : false
      const matchWeek = r.week != null ? String(r.week).toLowerCase().includes(q) : false
      const matchRev = r.weekly_revenue != null ? String(r.weekly_revenue).toLowerCase().includes(q) : false
      const matchPrem = r.mrg_premium != null ? String(r.mrg_premium).toLowerCase().includes(q) : false
      return matchStart || matchEnd || matchWeek || matchRev || matchPrem
    })
  }, [rows, searchQuery])

  const exportUrl = useMemo(() => {
    const qs = buildWeeklyReimbursementsExportParams({
      page: params.page,
      perPage: params.perPage,
      sortBy: params.sortBy,
      orderBy: params.orderBy,
      fromDt: params.fromDt,
      toDt: params.toDt,
      year: params.year,
      week_filter: params.week_filter,
    })
    return buildReportExportUrl('/api/export/claim_insurance', qs)
  }, [params])

  const fetchList = useCallback(async () => {
    setLoading(true)
    try {
      const qs = new URLSearchParams()
      appendWeeklyReimbursementsListParams(qs, params)
      writeWeeklyReimbursementsSession(
        buildWeeklyReimbursementsSessionFromParams(params),
      )
      const res = await getWeeklyReimbursementList(qs)
      setReportData(res)
      setRows(res.data ?? [])
      setPageMeta({ current: res.current, last: res.last, total: res.total })
    } catch (e) {
      showApiError(e)
    } finally {
      setLoading(false)
    }
  }, [params])

  useEffect(() => {
    void fetchList()
  }, [fetchList])

  const tc = reportData?.total_count

  return (
    <div data-testid="weekly-reimbursements-page">
      <PageShell
        title="Weekly Reimbursements Report"
        pageHeaderTestIdRoot="weekly-reimbursements-page-header"
        contentClassName="!space-y-6 w-full max-w-none min-w-0"
      >
        <ListSearchToolbar
          searchValue={searchQuery}
          searchPlaceholder="Search week, dates, revenue..."
          onSearchChange={setSearchQuery}
          activeFilterCount={countActiveWeeklyFilters(filterState)}
          onOpenFilters={() => setFiltersOpen(true)}
          testIdPrefix="weekly-reimbursements"
          actions={
            exportUrl ? (
              <a href={exportUrl} className={REPORT_EXPORT_BUTTON_CLASS}>
                <Download className="h-[18px] w-[18px]" />
                Export
              </a>
            ) : null
          }
        />

        <WeeklyReimbursementsFilters
          filters={filterState}
          isOpen={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          onApply={(next) => setParams((p) => ({ ...p, ...next, page: 1 }))}
          onReset={() => {
            clearWeeklyReimbursementsSession()
            setParams({ ...DEFAULT_PARAMS })
          }}
          onYearChange={(year) => applyYearChange(year, filterState)}
        />

        {tc ? (
          <WeeklyReimbursementsSummaryOverview
            totalCount={tc}
            reportData={reportData}
            loading={loading}
          />
        ) : null}

        <div className={cn(reportStyles.tableCard, 'w-full min-w-0')}>
          <ListPaginationBar
            placement="header"
            currentPage={pageMeta.current}
            lastPage={pageMeta.last}
            totalRecords={pageMeta.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            summaryLayout="angular"
            testIdPrefix="weekly-reimbursements"
          />
          <ReportTableScroll
            enabled={displayedRows.length > 0}
            testIdPrefix="weekly-reimbursements"
            className="touch-scroll"
          >
            <table
              className={cn(
                reportStyles.listingTable,
                'w-full min-w-[1250px] border-collapse text-left text-slate-800',
              )}
              data-testid="weekly-reimbursements-table"
            >
              <thead>
                {/* Tier 1 Header */}
                <tr>
                  <th
                    rowSpan={3}
                    className={cn(reportStyles.tableCellDivider, 'text-left font-bold text-slate-900')}
                  >
                    Start Date
                  </th>
                  <th
                    rowSpan={3}
                    className={cn(reportStyles.tableCellDivider, 'text-left font-bold text-slate-900')}
                  >
                    End Date
                  </th>
                  <th
                    rowSpan={3}
                    className={cn(reportStyles.tableCellDivider, 'text-center font-bold text-slate-900')}
                  >
                    Week
                  </th>
                  <th
                    rowSpan={3}
                    className={cn(reportStyles.tableCellDivider, 'text-right font-bold text-slate-900')}
                  >
                    Weekly Revenue
                  </th>
                  <th
                    rowSpan={3}
                    className={cn(reportStyles.tableGroupDivider, 'text-right font-bold text-slate-900')}
                  >
                    MRG Premium
                  </th>
                  <th
                    colSpan={2}
                    rowSpan={2}
                    className={cn(
                      reportStyles.groupHeaderTier1,
                      reportStyles.tableGroupDivider,
                    )}
                  >
                    Paid Reimbursements
                  </th>
                  <th
                    colSpan={4}
                    className={cn(
                      reportStyles.groupHeaderTier1,
                      reportStyles.groupHeaderAccent,
                      reportStyles.tableGroupDivider,
                    )}
                  >
                    Pending Reimbursements
                  </th>
                  <th
                    colSpan={3}
                    className={reportStyles.groupHeaderTier1}
                  >
                    Subtotal Reimbursements
                  </th>
                </tr>

                {/* Tier 2 Header */}
                <tr>
                  <th
                    colSpan={2}
                    className={cn(
                      reportStyles.groupHeaderTier2,
                      reportStyles.tableCellDivider,
                    )}
                  >
                    Under Review
                  </th>
                  <th
                    colSpan={2}
                    className={cn(
                      reportStyles.groupHeaderTier2,
                      reportStyles.tableGroupDivider,
                    )}
                  >
                    Docs Uploaded
                  </th>
                  <th
                    colSpan={3}
                    className={reportStyles.groupHeaderTier2}
                  >
                    Paid & Pending
                  </th>
                </tr>

                {/* Tier 3 Column Names */}
                <tr>
                  <th className={cn(reportStyles.tableCellDivider, 'text-center text-xs font-bold text-slate-800')}>
                    Count
                  </th>
                  <th className={cn(reportStyles.tableGroupDivider, 'text-right text-xs font-bold text-slate-800')}>
                    Amount
                  </th>
                  <th className={cn(reportStyles.tableCellDivider, 'text-center text-xs font-bold text-slate-800')}>
                    Count
                  </th>
                  <th className={cn(reportStyles.tableCellDivider, 'text-right text-xs font-bold text-slate-800')}>
                    Amount
                  </th>
                  <th className={cn(reportStyles.tableCellDivider, 'text-center text-xs font-bold text-slate-800')}>
                    Count
                  </th>
                  <th className={cn(reportStyles.tableGroupDivider, 'text-right text-xs font-bold text-slate-800')}>
                    Amount
                  </th>
                  <th className={cn(reportStyles.tableCellDivider, 'text-center text-xs font-bold text-slate-800')}>
                    Count
                  </th>
                  <th className={cn(reportStyles.tableCellDivider, 'text-right text-xs font-bold text-slate-800')}>
                    Amount
                  </th>
                  <th className="text-right text-xs font-bold text-slate-800">
                    % Wk Premium
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={14} className="py-12 text-center text-slate-500">
                      <Loader2 className="mx-auto h-6 w-6 animate-spin text-[rgb(21_151_255)]" />
                    </td>
                  </tr>
                ) : displayedRows.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="py-12 text-center text-slate-500 font-medium">
                      {searchQuery ? 'No matching records found' : 'No records found'}
                    </td>
                  </tr>
                ) : (
                  displayedRows.map((row, i) => (
                    <tr key={i}>
                      <td className={cn(reportStyles.tableBodyDivider, 'text-left font-medium text-slate-800')}>
                        {row.start_date ?? '—'}
                      </td>
                      <td className={cn(reportStyles.tableBodyDivider, 'text-left font-medium text-slate-800')}>
                        {row.end_date ?? '—'}
                      </td>
                      <td className={cn(reportStyles.tableBodyDivider, 'text-center text-slate-700')}>
                        {row.week ?? '—'}
                      </td>
                      <td className={cn(reportStyles.tableBodyDivider, 'text-right tabular-nums text-slate-900')}>
                        {formatAmount(row.weekly_revenue)}
                      </td>
                      <td className={cn(reportStyles.tableBodyGroupDivider, 'text-right tabular-nums font-medium text-slate-900')}>
                        {formatAmount(row.mrg_premium)}
                      </td>
                      <td className={cn(reportStyles.tableBodyDivider, 'text-center tabular-nums text-slate-800')}>
                        {row.claims_paid_count ?? 0}
                      </td>
                      <td className={cn(reportStyles.tableBodyGroupDivider, 'text-right tabular-nums font-medium text-slate-900')}>
                        {formatAmount(row.claims_paid)}
                      </td>
                      <td className={cn(reportStyles.tableBodyDivider, 'text-center tabular-nums text-slate-800')}>
                        {row.calculated_pending_claims_count ?? 0}
                      </td>
                      <td className={cn(reportStyles.tableBodyDivider, 'text-right tabular-nums text-slate-900')}>
                        {formatAmount(row.calculated_pending_claims)}
                      </td>
                      <td className={cn(reportStyles.tableBodyDivider, 'text-center tabular-nums text-slate-800')}>
                        {row.estimated_pending_claims_count ?? 0}
                      </td>
                      <td className={cn(reportStyles.tableBodyGroupDivider, 'text-right tabular-nums text-slate-900')}>
                        {formatAmount(row.estimated_pending_claims)}
                      </td>
                      <td className={cn(reportStyles.tableBodyDivider, 'text-center tabular-nums font-semibold text-slate-900')}>
                        {row.total_weekly_count_paid_n_pending ??
                          row.paid_n_pending_claims_count ??
                          0}
                      </td>
                      <td className={cn(reportStyles.tableBodyDivider, 'text-right tabular-nums font-semibold text-slate-900')}>
                        {formatAmount(
                          row.total_weekly_claims_paid_n_pending ??
                            row.paid_n_pending_claims,
                        )}
                      </td>
                      <td className="text-right tabular-nums font-semibold text-slate-900">
                        {pct(
                          row.claims_percent_of_premium ??
                            row.percent_of_weekly_premium,
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ReportTableScroll>
          <ListPaginationBar
            placement="footer"
            currentPage={pageMeta.current}
            lastPage={pageMeta.last}
            totalRecords={pageMeta.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            summaryLayout="angular"
            testIdPrefix="weekly-reimbursements"
          />
        </div>
      </PageShell>
    </div>
  )
}
